import { useLayoutEffect, useRef, useState, type InputHTMLAttributes } from "react";
import { formatMoneyInput, parseMoneyInput } from "../formatting";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "inputMode" | "value" | "defaultValue" | "onChange" | "onBlur" | "onPaste" | "onKeyDown"> & {
  value: string;
  onChange: (value: string) => void;
  locale?: "en-US" | "tr-TR";
};

export function MoneyInput({ value, onChange, min = "0", max, locale = "en-US", ...props }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  const [editing, setEditing] = useState(false);
  const displayed = formatMoneyInput(value, editing, locale);
  const grouping = locale === "tr-TR" ? "." : ",";
  const decimal = locale === "tr-TR" ? "," : ".";

  useLayoutEffect(() => {
    const field = input.current;
    if (!field) return;
    if (caret.current !== null) {
      field.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
    const amount = parseMoneyInput(value);
    const outOfRange = amount !== null && (Number(amount) < Number(min) || (max !== undefined && Number(amount) > Number(max)));
    field.setCustomValidity(value && (amount === null || outOfRange) ? `Geçerli aralıkta bir tutar girin. Örnek: ${formatMoneyInput("125000", false, locale)}` : "");
  }, [value, min, max, editing, locale]);

  return <input {...props} ref={input} type="text" inputMode="decimal" value={displayed}
    placeholder={props.placeholder || `0${decimal}00`}
    onChange={event => {
      const text = event.target.value;
      const native = event.nativeEvent as InputEvent;
      if (native.data && native.inputType !== "insertFromPaste" && /\D/.test(native.data.replaceAll(decimal, ""))) return;
      const ungrouped = text.replaceAll(grouping, "").replace(decimal, ".");
      const plain = ungrouped.startsWith(".") ? `0${ungrouped}` : ungrouped;
      if (!/^\d*(\.\d{0,2})?$/.test(plain)) return;
      const next = formatMoneyInput(plain, true, locale);
      let remaining = text.slice(0, event.target.selectionStart ?? text.length).replaceAll(grouping, "").length;
      if (plain !== ungrouped) remaining++;
      let position = 0;
      while (position < next.length && remaining > 0) {
        if (next[position] !== grouping) remaining--;
        position++;
      }
      caret.current = position;
      setEditing(true);
      onChange(plain);
    }}
    onPaste={event => {
      if (parseMoneyInput(event.clipboardData.getData("text"), locale) === null) event.preventDefault();
    }}
    onKeyDown={event => {
      const field = event.currentTarget;
      const start = field.selectionStart ?? 0;
      if (start !== field.selectionEnd) return;
      // Deleting beside a grouping separator should delete a digit, not stall.
      if (event.key === "Backspace" && field.value[start - 1] === grouping) field.setSelectionRange(start - 1, start - 1);
      if (event.key === "Delete" && field.value[start] === grouping) field.setSelectionRange(start + 1, start + 1);
    }}
    onBlur={() => {
      setEditing(false);
      const amount = parseMoneyInput(value.endsWith(".") ? `${value}00` : value);
      if (editing && amount !== null && amount !== value) onChange(amount);
    }}
  />;
}
